import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Bell, UserPlus, DollarSign, Calendar, Package } from 'lucide-react';
import api from '../../../services/authService';

const ActivityFeed = () => {
  // Fetch recent activity (combining recent bookings, vendors, and payments)
  const { data: activityData } = useQuery({
    queryKey: ['adminActivityFeed'],
    queryFn: async () => {
      const [bookings, vendors, payments] = await Promise.all([
        api.get('/admin/bookings?per_page=3&sort_by=created_at&sort_order=desc'),
        api.get('/admin/vendors?per_page=3&sort_by=created_at&sort_order=desc'),
        api.get('/admin/payments?per_page=3&sort_by=created_at&sort_order=desc')
      ]);

      const activities = [];

      // Add bookings
      (bookings.data?.data?.data || []).forEach(booking => {
        activities.push({
          id: `booking-${booking.id}`,
          type: 'booking',
          title: `New booking: ${booking.booking_reference || `#${booking.id.substring(0, 8)}`}`,
          description: booking.client?.name || 'Unknown client',
          time: booking.created_at,
          icon: Calendar,
          color: 'text-blue-600',
          bgColor: 'bg-blue-50'
        });
      });

      // Add vendors
      (vendors.data?.data?.data || []).forEach(vendor => {
        const vendorName = vendor.vendorProfile?.business_name || 
                          (vendor.first_name && vendor.last_name ? `${vendor.first_name} ${vendor.last_name}` : null) ||
                          vendor.name || 
                          vendor.email?.split('@')[0] || 
                          'Unknown Vendor';
        activities.push({
          id: `vendor-${vendor.id}`,
          type: 'vendor',
          title: `New vendor registered: ${vendorName}`,
          description: vendor.email || 'No email',
          time: vendor.created_at || vendor.vendorProfile?.created_at,
          icon: Package,
          color: 'text-purple-600',
          bgColor: 'bg-purple-50'
        });
      });

      // Add payments
      (payments.data?.data?.data || []).forEach(payment => {
        activities.push({
          id: `payment-${payment.id}`,
          type: 'payment',
          title: `Payment received: $${Number(payment.amount || 0).toFixed(2)}`,
          description: payment.booking?.booking_reference || 'Payment',
          time: payment.created_at,
          icon: DollarSign,
          color: 'text-green-600',
          bgColor: 'bg-green-50'
        });
      });

      // Sort by time and take most recent 5
      return activities
        .sort((a, b) => new Date(b.time) - new Date(a.time))
        .slice(0, 5);
    }
  });

  const activities = activityData || [];

  const formatTime = (dateString) => {
    if (!dateString) return 'Just now';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  if (activities.length === 0) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Activity</h3>
        <div className="text-center py-12">
          <Bell className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">No recent activity</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">Recent Activity</h3>
        <Bell className="w-5 h-5 text-gray-400" />
      </div>
      <div className="space-y-3">
        {activities.map((activity) => {
          const Icon = activity.icon;
          return (
            <div key={activity.id} className="flex items-start gap-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
              <div className={`w-8 h-8 ${activity.bgColor} rounded-lg flex items-center justify-center flex-shrink-0`}>
                <Icon className={`w-4 h-4 ${activity.color}`} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900">{activity.title}</p>
                <p className="text-xs text-gray-500 mt-0.5">{activity.description}</p>
                <p className="text-xs text-gray-400 mt-1">{formatTime(activity.time)}</p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ActivityFeed;

